<template>
  <div class="th-list-course">
    <h2 class="th-title"><b>Data Aset Kategori</b></h2>
    <a-card>
      <div class="mb-3 row">
        <div class="col-sm-4 col-md-3 m-1">
          <a-input-search placeholder="Cari Tipe Kategori..." v-model:value="search" allow-clear />
        </div>
        <div class="col m-1 w-100 d-flex justify-content-end align-items-center">
          <a-button type="primary" @click="openAddModal">
            <PlusOutlined />Tambah Kategori
          </a-button>
        </div>
      </div>
      <div class="w-100" style="overflow-x:auto;">
        <a-table
          :pagination="globalPagination"
          :columns="columns"
          :data-source="filteredCategoryTypes"
          class="table"
          bordered
          table-layout="auto"
          :loading="loadingTable"
          @change="handleTableChange"
        >
          <template #name="{ text, record }">
            <div>
              <a-input
                v-if="editingKey === record.id"
                v-model:value="editableData[record.id].name"
                style="margin: -5px 0"
                @pressEnter="save(record.id)"
              />
              <template v-else>
                {{ text }}
              </template>
            </div>
          </template>

          <template #action="{ record }">
            <div class="editable-row-operations">
              <span v-if="editingKey === record.id">
                <button class="btn btn-sm btn-outline-success mr-2" @click="save(record.id)">
                  <CheckOutlined /> Simpan
                </button>
                <a-popconfirm title="Yakin ingin membatalkan?" @confirm="cancel(record.id)">
                  <button class="btn btn-sm btn-outline-secondary"><CloseOutlined /> Batal</button>
                </a-popconfirm>
              </span>
              <span v-else>
                <button
                  class="btn btn-sm btn-outline-primary mr-2"
                  :disabled="editingKey !== ''"
                  @click="edit(record)"
                >
                  <EditOutlined /> Edit
                </button>
                <button
                  class="btn btn-sm btn-outline-danger"
                  :disabled="editingKey !== ''"
                  @click="openDeleteModal(record)"
                >
                  <DeleteOutlined /> Hapus
                </button>
              </span>
            </div>
          </template>
        </a-table>
      </div>
    </a-card>

    <a-modal
      v-model:visible="deleteModalVisible"
      title="Konfirmasi Penghapusan"
      @ok="confirmDelete"
      @cancel="cancelDelete"
      :confirm-loading="deleteLoading"
      ok-text="Hapus"
      cancel-text="Batal"
      ok-type="danger"
      centered
    >
      <p>
        <ExclamationCircleOutlined class="text-warning me-2" />
        Anda yakin ingin menghapus kategori aset
        <b>"{{ categoryToDelete ? categoryToDelete.name : '' }}"</b> ini secara permanen?
      </p>
      <p>Tindakan ini tidak dapat dibatalkan.</p>
    </a-modal>

    <a-modal
      v-model:visible="addModalVisible"
      title="Tambah Kategori Aset Baru"
      @ok="handleAddCategory"
      @cancel="cancelAdd"
      :confirm-loading="addLoading"
      ok-text="Tambah"
      cancel-text="Batal"
      centered
    >
      <a-form layout="vertical">
        <a-form-item label="Nama Kategori" required>
          <a-input
            v-model:value="newCategoryName"
            placeholder="Masukkan nama kategori"
            @pressEnter="handleAddCategory"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { onMounted, ref, computed, reactive } from 'vue'
import {
  PlusOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons-vue'
import {
  getAssetTypeCategory,
  updateAssetTypeCategory,
  deleteAssetTypeCategory,
  createAssetTypeCategory,
} from '../../../services/axios/modc/assets'
import lodash from 'lodash'
import { notification } from 'ant-design-vue'

export default {
  components: {
    PlusOutlined,
    ExclamationCircleOutlined,
    EditOutlined,
    DeleteOutlined,
    CheckOutlined,
    CloseOutlined,
  },
  setup() {
    const categoryTypes = ref([])
    const search = ref('')
    const loadingTable = ref(false)
    const globalPagination = ref({ current: 1, pageSize: 10 })

    const editableData = reactive({})
    const editingKey = ref('')

    const deleteModalVisible = ref(false)
    const categoryToDelete = ref(null)
    const deleteLoading = ref(false)

    const addModalVisible = ref(false)
    const newCategoryName = ref('')
    const addLoading = ref(false)

    const columns = ref([
      { title: 'Nama Kategori', dataIndex: 'name', key: 'name', slots: { customRender: 'name' } },
      {
        title: 'Aksi',
        key: 'action',
        slots: { customRender: 'action' },
        width: 220,
        align: 'center',
      },
    ])

    const fetchCategoryTypes = async () => {
      loadingTable.value = true
      try {
        const { categories } = await getAssetTypeCategory()
        categoryTypes.value = categories.map(cat => ({ ...cat, key: cat.id }))
        globalPagination.value.total = categories.length
      } catch (error) {
        console.error('Error fetching asset categories:', error)
        notification.error({
          message: 'Gagal Memuat',
          description: 'Gagal memuat data kategori aset.',
        })
      } finally {
        loadingTable.value = false
      }
    }

    onMounted(fetchCategoryTypes)

    const edit = record => {
      editableData[record.id] = lodash.cloneDeep(record)
      editingKey.value = record.id
    }

    const save = async id => {
      const target = categoryTypes.value.find(item => id === item.id)
      if (target && editableData[id]) {
        try {
          await updateAssetTypeCategory(id, { name: editableData[id].name })
          notification.success({
            message: 'Berhasil',
            description: `Kategori "${editableData[id].name}" telah diperbarui.`,
          })
          Object.assign(target, editableData[id])
          delete editableData[id]
          editingKey.value = ''
        } catch (error) {
          notification.error({
            message: 'Gagal',
            description: `Gagal memperbarui kategori. ${error.message || ''}`,
          })
        }
      }
    }

    const cancel = id => {
      delete editableData[id]
      editingKey.value = ''
    }

    const openDeleteModal = record => {
      categoryToDelete.value = record
      deleteModalVisible.value = true
    }

    const confirmDelete = async () => {
      if (!categoryToDelete.value) return
      deleteLoading.value = true
      try {
        await deleteAssetTypeCategory(categoryToDelete.value.id)
        notification.success({
          message: 'Berhasil',
          description: `Kategori "${categoryToDelete.value.name}" telah dihapus.`,
        })
        await fetchCategoryTypes()
        deleteModalVisible.value = false
      } catch (error) {
        notification.error({
          message: 'Gagal',
          description: `Gagal menghapus kategori. ${error.message || ''}`,
        })
      } finally {
        deleteLoading.value = false
        categoryToDelete.value = null
      }
    }

    const cancelDelete = () => {
      deleteModalVisible.value = false
      categoryToDelete.value = null
    }

    const openAddModal = () => {
      addModalVisible.value = true
      newCategoryName.value = ''
    }

    const handleAddCategory = async () => {
      if (!newCategoryName.value.trim()) {
        notification.warning({
          message: 'Peringatan',
          description: 'Nama kategori tidak boleh kosong!',
        })
        return
      }
      addLoading.value = true
      try {
        await createAssetTypeCategory(newCategoryName.value)
        notification.success({
          message: 'Berhasil',
          description: `Kategori "${newCategoryName.value}" telah ditambahkan.`,
        })
        addModalVisible.value = false
        await fetchCategoryTypes()
      } catch (error) {
        notification.error({
          message: 'Gagal',
          description: `Gagal menambahkan kategori. ${error.message || ''}`,
        })
      } finally {
        addLoading.value = false
      }
    }

    const cancelAdd = () => {
      addModalVisible.value = false
    }

    const filteredCategoryTypes = computed(() => {
      if (!search.value) return categoryTypes.value
      const lowerCaseSearch = search.value.toLowerCase()
      return categoryTypes.value.filter(category =>
        category.name.toLowerCase().includes(lowerCaseSearch),
      )
    })

    const handleTableChange = pag => {
      globalPagination.value.current = pag.current
    }

    return {
      search,
      globalPagination,
      columns,
      loadingTable,
      handleTableChange,
      filteredCategoryTypes,
      editingKey,
      editableData,
      edit,
      save,
      cancel,
      deleteModalVisible,
      categoryToDelete,
      deleteLoading,
      openDeleteModal,
      confirmDelete,
      cancelDelete,
      addModalVisible,
      newCategoryName,
      addLoading,
      openAddModal,
      handleAddCategory,
      cancelAdd,
    }
  },
}
</script>

<style lang="scss">
@import '@/views/styleViews.scss';

.editable-row-operations a {
  margin-right: 8px;
}

.text-warning {
  color: #faad14;
}

.text-success {
  color: #198754; /* Bootstrap success green */
}
</style>
